import React, { useState } from "react";
import { SingleEliminationTournament, BracketMatch } from "../types/tournament";
import {
  updateBracketMatch,
  getCurrentMatches,
  getTournamentProgress,
} from "../utils/bracketLogic";
import BracketDisplay from "./BracketDisplay";
import BracketScoreEntry from "./BracketScoreEntry";
import { RotateCcw, Trophy, Users, BarChart3 } from "lucide-react";

interface BracketDashboardProps {
  tournament: SingleEliminationTournament;
  setTournament: (tournament: SingleEliminationTournament) => void;
  onReset: () => void;
}

const BracketDashboard: React.FC<BracketDashboardProps> = ({
  tournament,
  setTournament,
  onReset,
}) => {
  const [selectedMatch, setSelectedMatch] = useState<BracketMatch | null>(null);
  const [activeView, setActiveView] = useState<"bracket" | "current" | "stats">(
    "bracket"
  );

  const currentMatches = getCurrentMatches(tournament);
  const progress = getTournamentProgress(tournament);

  const handleMatchClick = (match: BracketMatch) => {
    if (match.team1 && match.team2 && !match.isCompleted) {
      setSelectedMatch(match);
    }
  };

  const handleScoreSubmit = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    const updatedTournament = updateBracketMatch(
      tournament,
      matchId,
      team1Score,
      team2Score
    );
    setTournament(updatedTournament);
  };

  const isTournamentComplete = tournament.champion !== null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                🏆 {tournament.name}
              </h1>
              <p className="text-sm text-gray-600">
                Single Elimination Bracket • {tournament.teams.length} Teams •
                Round {tournament.currentRound} of {tournament.totalRounds}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={onReset}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-lg shadow-lg">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-4 px-6 overflow-x-auto">
                <button
                  onClick={() => setActiveView("bracket")}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeView === "bracket"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Bracket
                </button>
                <button
                  onClick={() => setActiveView("current")}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeView === "current"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Current Matches
                </button>
                <button
                  onClick={() => setActiveView("stats")}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeView === "stats"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Statistics
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="p-6">
              {activeView === "bracket" && (
                <BracketDisplay
                  tournament={tournament}
                  onMatchClick={handleMatchClick}
                />
              )}

              {activeView === "current" && (
                <div className="space-y-6">
                  {/* Tournament Progress */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Tournament Progress
                    </h3>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress.progress}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {progress.completedMatches} of {progress.totalMatches}{" "}
                        matches
                      </div>
                    </div>
                  </div>

                  {/* Current Matches */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Current Round Matches
                    </h3>
                    {currentMatches.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {currentMatches.map((match) => (
                          <div
                            key={match.id}
                            className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-300 cursor-pointer hover:shadow-xl transition-all duration-200"
                            onClick={() => handleMatchClick(match)}
                          >
                            <div className="text-center mb-4">
                              <h4 className="font-semibold text-gray-900">
                                Match {match.matchNumber}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {tournament.rounds[match.round - 1]?.roundName}
                              </p>
                            </div>

                            <div className="space-y-3">
                              <div className="text-center">
                                <div className="font-semibold text-gray-900">
                                  {match.team1?.name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {match.team1?.player1} &{" "}
                                  {match.team1?.player2}
                                </div>
                              </div>

                              <div className="text-center text-gray-500 font-medium">
                                VS
                              </div>

                              <div className="text-center">
                                <div className="font-semibold text-gray-900">
                                  {match.team2?.name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {match.team2?.player1} &{" "}
                                  {match.team2?.player2}
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 text-center">
                              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                Ready to Play
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">
                          {isTournamentComplete
                            ? "Tournament Complete!"
                            : "No matches ready to play"}
                        </p>
                        {isTournamentComplete && (
                          <div className="mt-4">
                            <p className="text-lg font-semibold text-green-600">
                              🏆 Champion: {tournament.champion?.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {tournament.champion?.player1} &{" "}
                              {tournament.champion?.player2}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeView === "stats" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Tournament Info
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <strong>Teams:</strong> {tournament.teams.length}
                        </div>
                        <div>
                          <strong>Rounds:</strong> {tournament.totalRounds}
                        </div>
                        <div>
                          <strong>Current Round:</strong>{" "}
                          {tournament.currentRound}
                        </div>
                        <div>
                          <strong>Progress:</strong>{" "}
                          {Math.round(progress.progress)}%
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Match Statistics
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <strong>Total Matches:</strong>{" "}
                          {progress.totalMatches}
                        </div>
                        <div>
                          <strong>Completed:</strong>{" "}
                          {progress.completedMatches}
                        </div>
                        <div>
                          <strong>Remaining:</strong>{" "}
                          {progress.totalMatches - progress.completedMatches}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Champion
                      </h4>
                      {tournament.champion ? (
                        <div>
                          <div className="font-semibold text-green-600">
                            {tournament.champion.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {tournament.champion.player1} &{" "}
                            {tournament.champion.player2}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500">TBD</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Score Entry Modal */}
      {selectedMatch && (
        <BracketScoreEntry
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          onSubmit={handleScoreSubmit}
        />
      )}
    </div>
  );
};

export default BracketDashboard;
